import { Flex, Text } from "@chakra-ui/react";
import React, { createContext, useContext } from "react";
import {
  ConfirmProps,
  useConfirmationDialog,
  UseConfirmationDialogProps,
} from "src/hooks/useConfirmationDialog";
import { useEditFormTemplateContext } from "../context";

type LiveEditContextValue = {
  confirmRequirement: () => Promise<boolean>;
  confirmAnswersOptions: () => Promise<boolean>;
  confirmQuestionVisibility: () => Promise<boolean>;
  confirmVerifications: () => Promise<boolean>;
};

const LiveEditConfirmationContext = createContext<
  LiveEditContextValue | undefined
>(undefined);

type ProviderProps = {
  questionId: string;
  children: React.ReactNode;
};
export const LiveEditConfirmationProvider = ({
  questionId,
  children,
}: ProviderProps) => {
  const Requirement = useLiveEditConfirmationInternal({
    questionId,
    includeUnpublishedQuestion: true,
    header: "Set as required:",
    body: (
      <>
        There may already be submitted responses on this form.
        <strong> Previously submitted forms will not be changed.</strong>
      </>
    ),
    confirmButton: {
      label: "Yes, set as required",
    },
  });

  const AnswersOptions = useLiveEditConfirmationInternal({
    questionId,
    includeUnpublishedQuestion: false,
    header: "Rename answer?",
    body: (
      <Flex direction="column" gap="4">
        <Text>
          Renaming this answer will impact any form responses using this answer,
          potentially <strong>changing its meaning.</strong>
        </Text>
        <Text>
          If you’d like to change the order of answers in the selection
          dropdown, consider <strong>moving the answer</strong> instead.
        </Text>
      </Flex>
    ),
    confirmButton: {
      label: "Yes, rename",
    },
  });

  const Verifications = useLiveEditConfirmationInternal({
    questionId,
    includeUnpublishedQuestion: true,
    header: "Add to existing verification?",
    body: (
      <>
        There may be responses already using this verification, where it may
        already be approved or rejected. Adding this question to the
        verification means this answer will also be shown as approved or
        rejected for those forms.
      </>
    ),
    confirmButton: {
      label: "Yes, add to verification",
    },
  });

  const QuestionVisibility = useLiveEditConfirmationInternal({
    questionId,
    includeUnpublishedQuestion: false,
    header: "Change conditional reveal?",
    body: (
      <>
        Changing the behavior on conditional reveal may impact submitted
        responses that are already complete. Changing conditional reveal may
        result in{" "}
        <strong>previously completed submissions to now be incomplete.</strong>
      </>
    ),
    confirmButton: {
      label: "Yes, change conditional reveal",
    },
  });

  const value = React.useMemo(
    () => ({
      confirmRequirement: Requirement.confirm,
      confirmAnswersOptions: AnswersOptions.confirm,
      confirmVerifications: Verifications.confirm,
      confirmQuestionVisibility: QuestionVisibility.confirm,
    }),
    [
      AnswersOptions.confirm,
      QuestionVisibility.confirm,
      Requirement.confirm,
      Verifications.confirm,
    ]
  );

  return (
    <LiveEditConfirmationContext.Provider value={value}>
      {children}
      {QuestionVisibility.confirmationDialog}
      {Requirement.confirmationDialog}
      {AnswersOptions.confirmationDialog}
      {Verifications.confirmationDialog}
    </LiveEditConfirmationContext.Provider>
  );
};

export const useLiveEditConfirmation = () => {
  const context = useContext(LiveEditConfirmationContext);
  if (!context) {
    throw new Error(
      "useLiveEditConfirmation must be used within a LiveEditConfirmationProvider"
    );
  }
  return context;
};

type Props = {
  questionId: string;
  includeUnpublishedQuestion: boolean;
} & UseConfirmationDialogProps;
const useLiveEditConfirmationInternal = ({
  includeUnpublishedQuestion,
  questionId,
  ...props
}: Props) => {
  const confirmation = useConfirmationDialog(props);
  const context = useEditFormTemplateContext();
  const [dismissed, setDismissed] = React.useState(false);

  const confirm = React.useCallback(
    async (props?: ConfirmProps) => {
      if (!context.isPublishedMode) {
        return true;
      }

      if (dismissed) {
        return true;
      }

      if (
        !includeUnpublishedQuestion &&
        context.helpers.isUnpublishedQuestion(questionId)
      ) {
        // Don't show confirmation for unpublished question
        return true;
      }

      const answer = await confirmation.confirm(props);
      if (answer) {
        setDismissed(true);
      }

      return answer;
    },
    [
      confirmation,
      context.helpers,
      context.isPublishedMode,
      dismissed,
      includeUnpublishedQuestion,
      questionId,
    ]
  );

  return {
    ...confirmation,
    confirm,
  };
};
